import React, { useState } from 'react'
import { KTIcon } from '../../../../../../../_metronic/helpers'
import { SmartTooltip } from '../../../../../../../components/SmartTooltip/SmartTooltip'
import { convertUTCtoMSK } from '../../../../../../../utils'
import { ISystemSetting } from '../../core/response.types'
import { updateSystemSetting } from '../../core/api'
import { extractSettingsError } from '../../core/errors'
import { ACTIONS_SLOT_WIDTH } from '../../core/consts'
import {
  DefaultHint,
  RangeSlider,
  sliderValueLabel,
} from '../../../../../../../components/RangeSlider/RangeSlider'

interface Props {
  setting: ISystemSetting
  service: string
}

export default function SystemSettingsRow({ setting, service }: Props) {
  const [committedValue, setCommittedValue] = useState(setting.value)
  const [editValue, setEditValue] = useState(setting.value)
  const [isDirty, setIsDirty] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [meta, setMeta] = useState({ updatedAt: setting.updatedAt, updatedBy: setting.updatedBy })

  const isBoolean = setting.type === 'boolean'
  const isEnum = setting.type === 'enum'
  const isJson = setting.type === 'json'
  const range = setting.type === 'slider' ? setting.range ?? null : null

  const defaultText = (() => {
    const d = setting.default
    if (d == null || d === '') return null
    if (isBoolean) return d === 'true' ? 'Да' : 'Нет'
    if (range) return sliderValueLabel(range, Number(d))
    return d
  })()

  const handleChange = (val: string) => {
    setEditValue(val)
    setIsDirty(val !== committedValue)
    setSaveError(null)
  }

  const handleSave = async () => {
    if (isJson) {
      try {
        JSON.parse(editValue)
      } catch {
        setSaveError('Некорректный JSON')
        return
      }
    }
    setIsSaving(true)
    setSaveError(null)
    try {
      const res = await updateSystemSetting(service, setting.key, editValue)
      if (!res?.data?.success) {
        throw res
      }
      const updated = res.data.data
      setCommittedValue(editValue)
      if (updated && (updated.updatedAt || updated.updatedBy)) {
        setMeta({ updatedAt: updated.updatedAt, updatedBy: updated.updatedBy })
      }
      setIsDirty(false)
    } catch (e: any) {
      console.error('Ошибка сохранения настройки:', e)
      setSaveError(extractSettingsError(e, 'Ошибка сохранения'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setEditValue(committedValue)
    setIsDirty(false)
    setSaveError(null)
  }

  return (
    <tr>
      {/* Название */}
      <td>
        <div className='d-flex align-items-center gap-2'>
          {setting.description ? (
            <SmartTooltip content={setting.description}>
              <span className='cursor-pointer text-muted'>
                <KTIcon iconName='information-5' className='fs-5' />
              </span>
            </SmartTooltip>
          ) : (
            <span style={{ width: 16 }} />
          )}
          <span className='fw-semibold'>{setting.label}</span>
        </div>
      </td>

      {/* Ключ */}
      <td>
        <code className='text-muted fs-7'>{setting.key}</code>
      </td>

      {/* Значение */}
      <td>
        <div className='d-flex flex-column gap-1'>
          <div className='d-flex align-items-center gap-2'>
            {setting.readOnly ? (
              <>
                <span className='text-gray-700'>{committedValue || '—'}</span>
                <SmartTooltip content='Только для чтения'>
                  <span className='text-muted'>
                    <KTIcon iconName='lock' className='fs-6' />
                  </span>
                </SmartTooltip>
              </>
            ) : isEnum ? (
              <select
                className='form-select form-select-sm form-select-solid w-auto'
                value={editValue}
                disabled={isSaving}
                onChange={(e) => handleChange(e.target.value)}
              >
                {(setting.options ?? []).map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : range ? (
              <RangeSlider
                range={range}
                value={Number(editValue)}
                disabled={isSaving}
                defaultValue={
                  setting.default != null && setting.default !== '' ? Number(setting.default) : null
                }
                onChange={(v) => handleChange(String(v))}
              />
            ) : isJson ? (
              <textarea
                className='form-control form-control-sm form-control-solid font-monospace'
                style={{ minWidth: 280 }}
                rows={3}
                value={editValue}
                disabled={isSaving}
                onChange={(e) => handleChange(e.target.value)}
              />
            ) : isBoolean ? (
              <select
                className='form-select form-select-sm form-select-solid w-auto'
                value={editValue}
                disabled={isSaving}
                onChange={(e) => handleChange(e.target.value)}
              >
                <option value='true'>Да</option>
                <option value='false'>Нет</option>
              </select>
            ) : (
              <input
                type='text'
                className='form-control form-control-sm form-control-solid'
                style={{ minWidth: 180 }}
                value={editValue}
                disabled={isSaving}
                onChange={(e) => handleChange(e.target.value)}
              />
            )}

            {!setting.readOnly && (
              // The action slot always takes its width, so showing the buttons never moves the column.
              <div
                className='d-flex align-items-center gap-2 flex-shrink-0'
                style={{ width: ACTIONS_SLOT_WIDTH }}
              >
                {isSaving ? (
                  <span className='spinner-border spinner-border-sm text-primary ms-1' />
                ) : isDirty ? (
                  <>
                    <button
                      className='btn btn-icon btn-sm btn-light-success'
                      title='Сохранить'
                      onClick={handleSave}
                    >
                      <KTIcon iconName='check' className='fs-4' />
                    </button>
                    <button
                      className='btn btn-icon btn-sm btn-light-danger'
                      title='Отмена'
                      onClick={handleCancel}
                    >
                      <KTIcon iconName='cross' className='fs-4' />
                    </button>
                  </>
                ) : null}
              </div>
            )}
          </div>
          {!setting.readOnly && <DefaultHint text={defaultText} />}
          {saveError && <span className='text-danger fs-8'>{saveError}</span>}
        </div>
      </td>

      {/* Дата и кем изменён */}
      <td>
        <div className='d-flex flex-column'>
          <span className='text-muted fs-7'>
            {meta.updatedAt ? convertUTCtoMSK(meta.updatedAt) : '—'}
          </span>
          {meta.updatedBy && <span className='text-muted fs-8'>{meta.updatedBy}</span>}
        </div>
      </td>
    </tr>
  )
}
